import { useQuery } from '@tanstack/react-query'
import { cn } from '@app/lib/cn'
import { syncApi, syncQueryKeys, type SyncStatusPayload } from './api'
import {
  formatCount,
  formatDataAsOf,
  formatDataAsOfLong,
  formatRelativeToNow,
  isStale,
  STALE_AFTER_HOURS,
} from './format'

/**
 * Topbar freshness chip — "Veriler: 11.08 18:57".
 *
 * Every screen in the panel reads a mirror of Korgun, not Korgun itself, so the
 * one thing a reader cannot infer from any table is how far that mirror
 * reaches. The chip states it once, in the shell, for every role: data age is
 * metadata, not money.
 *
 * Deliberately quiet. It sits at the muted end of the topbar in the same micro
 * type as the sidebar's database dot, and only earns colour when the mirror
 * falls more than a day behind — which is a fact worth interrupting for.
 *
 * It must never be able to break the shell: an error, a 401, a route that does
 * not exist yet, or a horizon the server cannot name all render nothing at all.
 */
export function SyncFreshness() {
  const { data, isPending, isError } = useQuery({
    queryKey: syncQueryKeys.status(),
    queryFn: () => syncApi.status(),
    // A sync run is a matter of minutes; a five-minute poll keeps a screen left
    // open on the shop floor honest without adding meaningful traffic.
    refetchInterval: 5 * 60_000,
  })

  /*
   * While the answer is in flight the chip holds its own footprint invisibly.
   * The topbar is a right-aligned row, so a chip that appeared with its width
   * would push the theme toggle and the user chip sideways under the cursor.
   */
  if (isPending) {
    return (
      <span aria-hidden className="invisible">
        <Chip label="00.00 00:00" stale={false} />
      </span>
    )
  }

  const dataAsOf = isError ? null : (data?.dataAsOf ?? null)
  const label = dataAsOf ? formatDataAsOf(dataAsOf) : null
  if (!data || !dataAsOf || !label) return null

  const stale = isStale(dataAsOf)
  return <Chip label={label} stale={stale} title={tooltip(data, dataAsOf, stale)} />
}

/**
 * The chip proper, factored out so the invisible placeholder above is the same
 * markup and therefore exactly the same width — including on narrow screens,
 * where the "Veriler:" label leaves the flow the same way in both.
 */
function Chip({ label, stale, title }: { label: string; stale: boolean; title?: string }) {
  return (
    <>
      <span
        title={title}
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-micro',
          stale ? 'text-hardal' : 'text-ink-muted',
        )}
      >
        <span
          aria-hidden
          className={cn('size-1.5 shrink-0 rounded-full', stale ? 'bg-hardal' : 'bg-ink-muted/40')}
        />
        {/* Below sm the label steps aside and the timestamp carries it alone;
            the tooltip and the reader stay whole. */}
        <span className="max-sm:sr-only">Veriler:</span>
        <span className="font-mono tnum">{label}</span>
        {stale ? <span className="sr-only">— {STALE_AFTER_HOURS} saatten eski</span> : null}
      </span>
      {/* House style is hairlines, not gaps, wherever two things differ in kind.
          It belongs to the chip so that a chip which renders nothing leaves no
          orphaned rule behind. */}
      <span aria-hidden className="h-4 w-px shrink-0 bg-hairline" />
    </>
  )
}

const SOURCE_LABEL: Record<SyncStatusPayload['source'], string> = {
  snapshot: 'Tam anlık görüntü',
  sync: 'Senkronizasyon',
}

/**
 * Native `title` — the shell's own tooltip idiom (nav items, theme toggle,
 * database dot all use it), and the only one that costs the topbar nothing.
 */
function tooltip(data: SyncStatusPayload, dataAsOf: string, stale: boolean): string {
  const lines: string[] = []

  const long = formatDataAsOfLong(dataAsOf)
  if (long) lines.push(`Veriler ${long} itibarıyla`)
  lines.push(`Kaynak: ${SOURCE_LABEL[data.source]}`)

  const relative = data.syncedAt ? formatRelativeToNow(data.syncedAt) : null
  if (relative) lines.push(`Son senkronizasyon: ${relative}`)

  const counts = runCounts(data.lastRun)
  if (counts) lines.push(counts)

  if (stale) lines.push(`Bu veriler ${STALE_AFTER_HOURS} saatten eski.`)

  return lines.join('\n')
}

/** "Tablo: 214 · Eklenen/güncellenen: 12.345 · Silinen: 3" — nulls stay out. */
function runCounts(lastRun: SyncStatusPayload['lastRun']): string | null {
  if (!lastRun) return null

  const parts: string[] = []
  if (lastRun.tablesTouched !== null) parts.push(`Tablo: ${formatCount(lastRun.tablesTouched)}`)
  if (lastRun.rowsUpserted !== null) {
    parts.push(`Eklenen/güncellenen: ${formatCount(lastRun.rowsUpserted)}`)
  }
  if (lastRun.rowsReplaced !== null) parts.push(`Yenilenen: ${formatCount(lastRun.rowsReplaced)}`)
  if (lastRun.rowsDeleted !== null) parts.push(`Silinen: ${formatCount(lastRun.rowsDeleted)}`)

  return parts.length === 0 ? null : parts.join(' · ')
}
